/**
 * 路由守卫：未登录买家拦截为「登录提示浮窗」，不再生硬跳转。
 * ---------------------------------------------------------------------------
 * 用法（置于 BuyerLayout 分组之外包裹整个分组）：
 *   <Route element={<RequireAuth><BuyerLayout /></RequireAuth>}>
 *     <Route path="/buyer/orders" element={<OrderPayment />} />
 *     ...
 *   </Route>
 * 实现要点：
 *   · 始终渲染 children，让当前路由内容持续作为背景可见；
 *   · 未登录时叠加 LoginPromptDialog（半透明 backdrop ≈ 35%）；
 *   · 「随便看看」= 关闭浮窗回公共首页；「去登录」= 带 state.from 跳登录页。
 *   · 已登录 → 直接渲染子页面。
 * ---------------------------------------------------------------------------
 */

import { useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useBuyerAuth } from '@/store/BuyerAuthContext';
import LoginPromptDialog from '@/components/buyer/LoginPromptDialog';

interface RequireAuthProps {
  children: ReactNode;
  /** Do not mount protected interactive flows before sign-in. */
  blockUnauthenticated?: boolean;
}

export default function RequireAuth({ children, blockUnauthenticated = false }: RequireAuthProps) {
  const { isAuthenticated } = useBuyerAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <>
      {/* 始终渲染 children，保持当前页面作为背景 */}
      {(isAuthenticated || !blockUnauthenticated) && children}

      {!isAuthenticated && (
        <LoginPromptDialog
          open
          onClose={() => navigate('/buyer/home')}
          from={location.pathname + location.search + location.hash}
          returnState={location.state}
        />
      )}
    </>
  );
}
